import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useBranding from "../../../hooks/useBranding";
import whiteLogo from "../../../assets/boj-images/white_boj_icon.png";
import darkLogo from "../../../assets/boj-images/dark_boj.png";
import {
  FaTachometerAlt,
  FaMapMarkedAlt,
  FaStore,
  FaUsers,
  FaTasks,
  FaIdBadge,
  FaCog,
  FaSignOutAlt,
  FaChevronDown,
  FaBars,
  FaTimes,
} from "react-icons/fa";

function ExhibitorDashboardNavbar() {
  const { coverPicUrl, logoUrl } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileShow, setMobileShow] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const closeTimeoutRef = useRef(null);

  const handleMouseEnter = (key) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdown(key);
  };

  const handleMouseLeave = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleDropdownToggle = (key) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdown((prev) => (prev === key ? null : key));
  };

  const handleItemClick = (path) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdown(null);
    setMobileShow(false);
    navigate(path);
  };

  const handleLogout = () => {
    if (window.portalAuth) {
      window.portalAuth.logout('superadmin');
    } else {
      localStorage.removeItem("jff_token");
    }
    navigate("/superadmin/login");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="crm-navbar-container">
      <div className="crm-navbar">
        {/* Professional Top Banner Image Logo */}
        <div
          className="crm-nav-brand"
          onClick={() => navigate("/superadmin/dashboard")}
        >
          <img
            src={coverPicUrl || logoUrl || whiteLogo}
            alt="The BOJ Show"
            className="crm-brand-banner-logo"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = darkLogo;
            }}
          />
        </div>

        {/* Mobile Menu Button */}
        <button
          className="crm-mobile-toggle"
          onClick={() => setMobileShow(!mobileShow)}
          aria-label="Toggle Navigation"
        >
          {mobileShow ? <FaTimes /> : <FaBars />}
        </button>

        {/* Horizontal Nav Items */}
        <ul className={`crm-nav-menu ${mobileShow ? "show" : ""}`}>
          {/* Dashboard */}
          <li>
            <button
              className={`crm-nav-button ${isActive("/superadmin/dashboard") ? "active" : ""}`}
              onClick={() => {
                if (closeTimeoutRef.current) {
                  clearTimeout(closeTimeoutRef.current);
                  closeTimeoutRef.current = null;
                }
                setActiveDropdown(null);
                setMobileShow(false);
                navigate("/superadmin/dashboard");
              }}
              onMouseEnter={() => {
                if (closeTimeoutRef.current) {
                  clearTimeout(closeTimeoutRef.current);
                  closeTimeoutRef.current = null;
                }
                setActiveDropdown(null);
              }}
            >
              <FaTachometerAlt /> Dashboard
            </button>
          </li>

          {/* Area */}
          <li
            className={`crm-nav-item-dropdown ${activeDropdown === "area" ? "show" : ""}`}
            onMouseEnter={() => handleMouseEnter("area")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className="crm-nav-button"
              onClick={() => handleDropdownToggle("area")}
            >
              <FaMapMarkedAlt /> Area <FaChevronDown size={10} />
            </button>
            <div
              className="crm-dropdown-menu"
              onMouseEnter={() => handleMouseEnter("area")}
              onMouseLeave={handleMouseLeave}
            >
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/add-area") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/add-area")}
              >
                Add Area
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/area-list") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/area-list")}
              >
                Area List
              </div>
            </div>
          </li>

          {/* Exhibitor */}
          <li
            className={`crm-nav-item-dropdown ${activeDropdown === "exhibitor" ? "show" : ""}`}
            onMouseEnter={() => handleMouseEnter("exhibitor")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className="crm-nav-button"
              onClick={() => handleDropdownToggle("exhibitor")}
            >
              <FaStore /> Exhibitor <FaChevronDown size={10} />
            </button>
            <div
              className="crm-dropdown-menu"
              onMouseEnter={() => handleMouseEnter("exhibitor")}
              onMouseLeave={handleMouseLeave}
            >
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/add-exhibitor") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/add-exhibitor")}
              >
                Add Exhibitor
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/exhibitor-list") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/exhibitor-list")}
              >
                Exhibitor List
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/exhibitor-report") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/exhibitor-report")}
              >
                Exhibitor Report
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/vip-guest") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/vip-guest")}
              >
                Buyers Request List
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/pre-approved-vip-guest") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/pre-approved-vip-guest")}
              >
                Pre Approved Buyer List
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/invited-vip-guest") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/invited-vip-guest")}
              >
                Approved Buyer List
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/management-assistant-list") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/management-assistant-list")}
              >
                Management & Assistant List
              </div>
            </div>
          </li>

          {/* Visitor */}
          <li
            className={`crm-nav-item-dropdown ${activeDropdown === "visitor" ? "show" : ""}`}
            onMouseEnter={() => handleMouseEnter("visitor")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className="crm-nav-button"
              onClick={() => handleDropdownToggle("visitor")}
            >
              <FaUsers /> Visitor <FaChevronDown size={10} />
            </button>
            <div
              className="crm-dropdown-menu"
              onMouseEnter={() => handleMouseEnter("visitor")}
              onMouseLeave={handleMouseLeave}
            >
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/vis-req-li") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/vis-req-li")}
              >
                Visitor Request List
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/acc-vis-li") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/acc-vis-li")}
              >
                Accepted Visited List
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/hol-vis-li") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/hol-vis-li")}
              >
                Hold Visitor List
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/del-vis-li") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/del-vis-li")}
              >
                Rejected Visited List
              </div>
            </div>
          </li>

          {/* Management */}
          <li
            className={`crm-nav-item-dropdown ${activeDropdown === "management" ? "show" : ""}`}
            onMouseEnter={() => handleMouseEnter("management")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className="crm-nav-button"
              onClick={() => handleDropdownToggle("management")}
            >
              <FaTasks /> Management <FaChevronDown size={10} />
            </button>
            <div
              className="crm-dropdown-menu"
              onMouseEnter={() => handleMouseEnter("management")}
              onMouseLeave={handleMouseLeave}
            >
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/cab-mangement-list") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/cab-mangement-list")}
              >
                Cab Management List
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/hotel-management-list") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/hotel-management-list")}
              >
                Hotel Management List
              </div>
            </div>
          </li>

          {/* Extra Badges */}
          <li>
            <button
              className={`crm-nav-button ${isActive("/superadmin/extra-badges") ? "active" : ""}`}
              onClick={() => {
                if (closeTimeoutRef.current) {
                  clearTimeout(closeTimeoutRef.current);
                  closeTimeoutRef.current = null;
                }
                setActiveDropdown(null);
                setMobileShow(false);
                navigate("/superadmin/extra-badges");
              }}
              onMouseEnter={() => {
                if (closeTimeoutRef.current) {
                  clearTimeout(closeTimeoutRef.current);
                  closeTimeoutRef.current = null;
                }
                setActiveDropdown(null);
              }}
            >
              <FaIdBadge /> Extra Badges
            </button>
          </li>

          {/* Settings */}
          <li
            className={`crm-nav-item-dropdown ${activeDropdown === "setting" ? "show" : ""}`}
            onMouseEnter={() => handleMouseEnter("setting")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className="crm-nav-button"
              onClick={() => handleDropdownToggle("setting")}
            >
              <FaCog /> Setting <FaChevronDown size={10} />
            </button>
            <div
              className="crm-dropdown-menu"
              onMouseEnter={() => handleMouseEnter("setting")}
              onMouseLeave={handleMouseLeave}
            >
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/stall-config-list") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/stall-config-list")}
              >
                Stall Configuration List
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/registration-setting") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/registration-setting")}
              >
                Registration Setting
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/branding-setting") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/branding-setting")}
              >
                Branding Settings
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/import-exhibitors") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/import-exhibitors")}
              >
                Import Exhibitors
              </div>
              <div
                className={`crm-dropdown-item ${isActive("/superadmin/download-pics-zip") ? "active" : ""}`}
                onClick={() => handleItemClick("/superadmin/download-pics-zip")}
              >
                Download Pics
              </div>
            </div>
          </li>
        </ul>

        {/* Actions (Logout) */}
        <div className="crm-nav-actions">
          <button className="crm-logout-btn" onClick={handleLogout} title="Logout">
            <FaSignOutAlt />
          </button>
        </div>
      </div>
    </header>
  );
}

export default ExhibitorDashboardNavbar;
